import { useMemo } from 'react';
import { Location } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { CgHome, CgFileDocument, CgFile } from 'react-icons/cg';
import { PageState } from './usePageState';

// ============= Types =============
export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
}

// ============= Constants =============
const BASE_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: <CgHome size={18} />,
  },
  {
    id: 'activities',
    label: 'Activities',
    path: '/activities',
    icon: <CgFileDocument size={18} />,
  },
  {
    id: 'files',
    label: 'Files',
    path: '/files',
    icon: <CgFile size={18} />,
  },
];

// ============= Hook =============
/**
 * Custom hook to generate navigation items based on current page state
 * Extracts all the complex navigation logic from BodyLayout
 */
export const useNavigationItems = (
  pageState: PageState,
  location: Location
): NavigationItem[] => {
  return useMemo(() => {
    // Always include base navigation items
    const items = [...BASE_NAVIGATION_ITEMS];

    // If we're on a request details page, modify the Activities and Files links to include the request ID
    // and add a link back to the workflows
    if (pageState.isRequestDetailsPage && pageState.requestId) {
      // Add a link back to workflows
      const parts = location.pathname.split('/');
      const workflowKeyForBack =
        parts[1] === 'request' && parts.length > 3 ? parts[2] : null;
      if (workflowKeyForBack) {
        items.push({
          id: 'workflows',
          label: 'Back to Workflows',
          path: `/workflows/${workflowKeyForBack}`,
          icon: <IoMdArrowBack size={18} />,
        });
      }

      // Modify the Activities link to include the workflowType and request ID
      const activitiesIndex = items.findIndex(
        (item) => item.id === 'activities'
      );
      if (activitiesIndex !== -1) {
        items[activitiesIndex] = {
          ...items[activitiesIndex],
          path: `/activities/${workflowKeyForBack}/${pageState.requestId}`,
        };
      }

      // Modify the Files link to include the workflowType and request ID
      const filesIndex = items.findIndex((item) => item.id === 'files');
      if (filesIndex !== -1) {
        items[filesIndex] = {
          ...items[filesIndex],
          path: `/files/${workflowKeyForBack}/${pageState.requestId}`,
        };
      }
    }

    // If we're on activities page with an ID, modify the Files link to include the same ID
    if (pageState.isActivitiesPage && pageState.activityId) {
      // Extract workflowType from the URL path
      const parts = location.pathname.split('/');
      const workflowType = parts.length > 2 ? parts[2] : null;

      if (workflowType) {
        items.push({
          id: 'workflows',
          label: 'Back to Workflows',
          path: `/workflows/${workflowType}`,
          icon: <IoMdArrowBack size={18} />,
        });

        const filesIndex = items.findIndex((item) => item.id === 'files');
        if (filesIndex !== -1) {
          items[filesIndex] = {
            ...items[filesIndex],
            path: `/files/${workflowType}/${pageState.activityId}`,
          };
        }
      }
    }

    // If we're on files page with an ID, modify the Activities link to include the same ID
    if (pageState.isFilesPage && pageState.fileId) {
      // Extract workflowType from the URL path
      const parts = location.pathname.split('/');
      const workflowType = parts.length > 2 ? parts[2] : null;

      if (workflowType) {
        items.push({
          id: 'workflows',
          label: 'Back to Workflows',
          path: `/workflows/${workflowType}`,
          icon: <IoMdArrowBack size={18} />,
        });

        const activitiesIndex = items.findIndex(
          (item) => item.id === 'activities'
        );
        if (activitiesIndex !== -1) {
          items[activitiesIndex] = {
            ...items[activitiesIndex],
            path: `/activities/${workflowType}/${pageState.fileId}`,
          };
        }
      }
    }

    // If we're in a workflow, add workflow-specific items
    if (pageState.isWorkflowPage && pageState.currentWorkflow) {
      // Add the current workflow as a navigation item
      items.push({
        id: pageState.currentWorkflow.path?.replace('/workflows/', '') || '',
        label: pageState.currentWorkflow.name,
        path: pageState.currentWorkflow.path || '',
        icon: <span>{pageState.currentWorkflow.icon}</span>,
      });
    }

    return items;
  }, [
    pageState.isRequestDetailsPage,
    pageState.requestId,
    pageState.isActivitiesPage,
    pageState.activityId,
    pageState.isFilesPage,
    pageState.fileId,
    pageState.isWorkflowPage,
    pageState.currentWorkflow,
    location.pathname,
  ]);
};
